import {
  AutoComplete,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
} from 'antd';
import { FC, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SearchFleetCustomer,
  CustomerGroupMember,
  BmppGroupRelationship,
  searchCustomerForGroup,
} from '../../../libs/fleet-api/services/customer';
import useDebounce from '../../../hooks/useDebounce';
import ANTD_DEFAULT_PROPS from '../../../libs/antd/props';
import FleetTitle from '@/libs/antd/components/Title';
import { formatNPWPValue } from '@/libs/antd/formater';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  existingCustomerIds?: string[];
  onSelect: (
    member: CustomerGroupMember & {
      customerName?: string;
      npwpNo?: string;
      jfFinancingLimitAmount?: number;
      njfFinancingLimitAmount?: number;
      jfNtfAmount?: number;
      njfNtfAmount?: number;
      jfOutstandingPrincipalAmount?: number;
      njfOutstandingPrincipalAmount?: number;
      jfRemainingLimitAmount?: number;
      njfRemainingLimitAmount?: number;
    },
  ) => void;
}

const CustomerGroupAddMemberModal: FC<Props> = ({
  isOpen,
  onClose,
  existingCustomerIds = [],
  onSelect,
}) => {
  const MIN_SEARCH_CHARS = 3;
  const [debouncedValue, setDebouncedValue] = useState('');
  const debouncedSet = useDebounce((val: string) => setDebouncedValue(val), 500);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['searchCustomerForGroup', debouncedValue] as const,
    queryFn: () =>
      searchCustomerForGroup({
        search: { by: 'customer-name', value: debouncedValue },
      }),
    enabled: isOpen && debouncedValue.length >= MIN_SEARCH_CHARS,
  });

  const [form] = Form.useForm();
  const [selectedCustomer, setSelectedCustomer] = useState<SearchFleetCustomer | null>(null);

  const memberTypeShort: Record<string, string> = {
    estar: 'E',
    corporate: 'C',
    retail: 'R',
    local: 'L',
  };

  const memberTypeColor: Record<string, string> = {
    estar: '#1557BC',
    local: '#C59B04',
  };

  const options = useMemo(() => {
    const items = data?.data ?? [];
    const existingSet = new Set(existingCustomerIds);
    const seen = new Set<string>();
    const result: {
      label: React.ReactNode;
      value: string;
      customer: SearchFleetCustomer;
    }[] = [];

    for (const c of items) {
      const id = c.customerId;
      if (!id) continue;
      if (existingSet.has(id)) continue;
      if (seen.has(id)) continue;
      seen.add(id);

      const typeKey = (c.memberType ?? '').toString().toLowerCase();
      const typeShort = memberTypeShort[typeKey];
      const typeColor = memberTypeColor[typeKey];

      result.push({
        label: (
          <div style={{ lineHeight: 1.3, padding: '4px 0' }}>
            <div>
              {typeShort && (
                <span
                  style={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: typeColor,
                  }}
                >
                  [{typeShort}]{' '}
                </span>
              )}
              {c.customerName}
            </div>
            <div style={{ color: '#999', fontSize: 12 }}>{id}</div>
          </div>
        ),
        value: id,
        customer: c,
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, existingCustomerIds]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      if (selectedCustomer) {
        onSelect({
          customerId: selectedCustomer.customerId,
          customerName: selectedCustomer.customerName,
          npwpNo: selectedCustomer.npwpNo,
          relationshipId: values.relationshipId,
          memberType: selectedCustomer.memberType ?? 'local',
          jfFinancingLimitAmount: selectedCustomer.jfFinancingLimitAmount,
          njfFinancingLimitAmount: selectedCustomer.njfFinancingLimitAmount,
          jfNtfAmount: selectedCustomer.jfNtfAmount,
          njfNtfAmount: selectedCustomer.njfNtfAmount,
          jfOutstandingPrincipalAmount: selectedCustomer.jfOutstandingPrincipalAmount,
          njfOutstandingPrincipalAmount: selectedCustomer.njfOutstandingPrincipalAmount,
          jfRemainingLimitAmount: selectedCustomer.jfRemainingLimitAmount,
          njfRemainingLimitAmount: selectedCustomer.njfRemainingLimitAmount,
        });
        handleClose();
      }
    });
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedCustomer(null);
    setDebouncedValue('');
    onClose();
  };

  return (
    <Modal
      title={<FleetTitle level={2}>Add Member of Group Customer BMPP</FleetTitle>}
      open={isOpen}
      onCancel={handleClose}
      width={960}
      footer={[
        <Button key="back" onClick={handleClose}>
          BACK
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSave}
          disabled={!selectedCustomer}
        >
          SAVE
        </Button>,
      ]}
    >
      <Divider />
      <Form
        form={form}
        {...ANTD_DEFAULT_PROPS.FORM}
        initialValues={{ relationshipId: undefined }}
      >
        <Row gutter={[20, 20]}>
          <Col span={12}>
            <Form.Item
              label="Customer Name"
              name="customerName"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              rules={[{ required: true, message: 'Please select a customer' }]}
            >
              <AutoComplete
                options={options}
                notFoundContent={
                  debouncedValue.length < MIN_SEARCH_CHARS
                    ? `Ketik minimal ${MIN_SEARCH_CHARS} karakter untuk mencari`
                    : !isLoading
                      ? 'Tidak ada data'
                      : null
                }
                onChange={(val) => {
                  if (val && val.length >= MIN_SEARCH_CHARS) {
                    debouncedSet(val);
                  } else {
                    setDebouncedValue('');
                  }
                  if (!val) {
                    setSelectedCustomer(null);
                    form.resetFields(['customerId', 'npwpNo']);
                  }
                }}
                onSelect={(_val, option) => {
                  const c = (option as typeof option & { customer: SearchFleetCustomer }).customer;
                  setSelectedCustomer(c);
                  form.setFieldsValue({
                    customerId: c.customerId,
                    customerName: c.customerName,
                    npwpNo: c.npwpNo ? formatNPWPValue(c.npwpNo) : '',
                  });
                }}
                placeholder="Ketik nama customer..."
              >
                <Input suffix={isLoading || isFetching ? <Spin size="small" /> : null} />
              </AutoComplete>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Kelompok BMPP"
              name="relationshipId"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              rules={[{ required: true, message: 'Please select Kelompok BMPP' }]}
            >
              <Select placeholder="Select Kelompok BMPP">
                <Select.Option value={BmppGroupRelationship.Parent}>Pihak Terkait</Select.Option>
                <Select.Option value={BmppGroupRelationship.Subsidiary}>Bukan Pihak Terkait</Select.Option>
                <Select.Option value={BmppGroupRelationship.Affiliate}>Kelompok Bukan Pihak Terkait</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[20, 20]} style={{ marginBottom: 40 }}>
          <Col span={12}>
            <Form.Item
              label="Customer ID"
              name="customerId"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
            >
              <Input readOnly variant="borderless" style={{ padding: 0 }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="NPWP"
              name="npwpNo"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
            >
              <Input readOnly variant="borderless" style={{ padding: 0 }} />
            </Form.Item>
          </Col>
        </Row>

      </Form>
    </Modal>
  );
};

export default CustomerGroupAddMemberModal;
